import type { Control, FieldErrors } from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { NumericTextField } from 'ui/components/NumericTextField'
import { CreateFormGridItem } from 'ui/ManagerFinance/components'
import styled from 'styled-components'
import { formatCurrency } from 'util/formatStrings'
import { ErrorMessage } from 'ui/ManagerFinance/utils/components'
import type { LoanEventCreateFormInputs } from './validation'
import { FormattedMessage, useIntl } from 'react-intl'
import type { Loan, LoanCalculation } from '@viilu-web/shared'

const NotificationMessage = styled.small`
	font-size: 0.7em;
`

type Props = {
	loan: Loan
	shareGroupUUID: string | undefined | null
	control: Control<LoanEventCreateFormInputs>
	errors: FieldErrors<LoanEventCreateFormInputs>
	disabled: boolean
	drawEventLimitReached: boolean
	calculation?: LoanCalculation
}

function LoanEventCreateAdjustedAmountInput({
	loan,
	control,
	errors,
	disabled,
	calculation,
	drawEventLimitReached,
}: Props) {
	const intl = useIntl()

	const [eventDate] = useWatch({
		name: ['eventDate'],
		control,
	})

	// Case 1: loan was fully drawn from the beginning
	if (
		loan.originalLoanAmount &&
		loan.amountDrawn &&
		loan.originalLoanAmount <= loan.amountDrawn
	) {
		return (
			<NotificationMessage>
				<FormattedMessage
					defaultMessage="Laina on jo kokonaan nostettu."
					description="Notification message when loan is fully drawn"
				/>
			</NotificationMessage>
		)
	}

	// Case 2: loan wasnt fully drawn at beginning, but draw limit is reached from draw events (created at any point in time)
	if (drawEventLimitReached) {
		return (
			<NotificationMessage>
				<FormattedMessage
					defaultMessage="Laina nostotapahtumat on jo luotu. Et voi luoda enää uusia lainan nostotapahtumia."
					description="Notification message when draw limit is reached"
				/>
			</NotificationMessage>
		)
	}

	// Case 3: event date is not selected yet
	if (!eventDate) {
		return (
			<NotificationMessage>
				<FormattedMessage
					defaultMessage="Valitse ensin tapahtuman päivämäärä, jotta voit määrittää noston määrän."
					description="Notification message when event date is not selected"
				/>
			</NotificationMessage>
		)
	}

	const amountKey = 'amount'

	return (
		<>
			<CreateFormGridItem
				key={`${amountKey}-helper`}
				$fullWidth={true}
				style={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<NotificationMessage>
					<FormattedMessage
						defaultMessage={`Lainan alkuperäinen pääoma: {originalLoanAmount}`}
						description="Sharegroup/apartment current loan share"
						values={{
							originalLoanAmount: formatCurrency(
								calculation?.originalLoanAmount || 0,
							),
						}}
					/>
				</NotificationMessage>

				<NotificationMessage>
					<FormattedMessage
						defaultMessage={`Lainaa nostettu yhteensä: {amountDrawn}`}
						description="Sharegroup/apartment current loan share"
						values={{
							amountDrawn: formatCurrency(calculation?.loanDrawnAmount || 0),
						}}
					/>
				</NotificationMessage>
			</CreateFormGridItem>
			<CreateFormGridItem key={amountKey} $fullWidth={true}>
				<Controller
					name={amountKey}
					control={control}
					render={({ field: { onChange, value: currentValue } }) => (
						<NumericTextField
							id="units-numeric-input"
							label={intl.formatMessage({
								defaultMessage: 'Nostettava lainasumma (€)',
								description: 'Units',
							})}
							required={true}
							lineDirection="center"
							floating
							fullWidth
							error={errors[amountKey] !== undefined}
							disabled={disabled}
							value={currentValue as number}
							onChange={(value) => {
								onChange(value)
							}}
							min={0}
						/>
					)}
				/>
				{errors[amountKey]?.message && (
					<ErrorMessage style={{ marginBottom: '0.5rem' }}>
						{errors[amountKey]?.message}
					</ErrorMessage>
				)}
			</CreateFormGridItem>
		</>
	)
}

export default LoanEventCreateAdjustedAmountInput
